import { useState } from "react";
import {
  useClientsQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useDeleteClientMutation,
  parseClientStyle,
  type Client,
} from "@/api/clients";
import type { StyleProfile } from "@/api/settings";

/** Clients card for the Settings page: manage the brands the team produces
 *  work for, each with its own style profile. Selecting a client on a
 *  project (workspace → Settings → Brand style) injects that profile into
 *  generations instead of the team default. */
export function ClientsCard() {
  const { data: clients } = useClientsQuery();
  const createClient = useCreateClientMutation();
  const updateClient = useUpdateClientMutation();
  const deleteClient = useDeleteClientMutation();

  // null = closed; "new" = creating; otherwise the client id being edited.
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [style, setStyle] = useState<StyleProfile>({});
  const [error, setError] = useState<string | null>(null);

  const openNew = () => {
    setEditing("new");
    setName("");
    setStyle({});
    setError(null);
  };
  const openEdit = (client: Client) => {
    setEditing(client.id);
    setName(client.name);
    setStyle(parseClientStyle(client));
    setError(null);
  };
  const close = () => setEditing(null);

  const save = async () => {
    setError(null);
    try {
      if (editing === "new") {
        await createClient.mutateAsync({ name, style });
      } else if (editing) {
        await updateClient.mutateAsync({ id: editing, name, style });
      }
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save client");
    }
  };

  const remove = async (client: Client) => {
    if (
      !window.confirm(
        `Remove client "${client.name}"? Projects using its style fall back to the team default.`,
      )
    )
      return;
    try {
      await deleteClient.mutateAsync(client.id);
      if (editing === client.id) close();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to remove client");
    }
  };

  const saving = createClient.isPending || updateClient.isPending;

  const styleFields = [
    {
      key: "art_style" as const,
      label: "Art style",
      placeholder: "e.g. flat corporate illustration, soft gradients",
    },
    {
      key: "motion" as const,
      label: "Motion & timing",
      placeholder: "e.g. calm, understated easing; nothing bouncy",
    },
    {
      key: "palette" as const,
      label: "Palette",
      placeholder: "e.g. #003366 navy base, #ff6600 accent, white text",
    },
    {
      key: "typography" as const,
      label: "Typography",
      placeholder: "e.g. Inter for everything, sentence case",
    },
  ];

  return (
    <div className="mt-6 rounded-xl border border-line bg-surface-2">
      <div className="flex items-center justify-between border-b border-line px-6 py-4">
        <h2 className="text-sm font-semibold text-ink">Clients</h2>
        <button
          onClick={openNew}
          className="rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover"
        >
          Add client
        </button>
      </div>
      <div className="space-y-4 p-6">
        <p className="text-xs text-ink-3">
          The brands you produce work for. Each client carries its own style
          and branding; pick the client on a project (workspace → Settings →
          Brand style) and generations follow their brand instead of your
          team's.
        </p>

        {(clients ?? []).length === 0 && editing === null && (
          <p className="text-xs text-ink-3">No clients yet.</p>
        )}

        <ul className="space-y-2">
          {(clients ?? []).map((client) => (
            <li
              key={client.id}
              className="flex items-center justify-between rounded-lg border border-line bg-surface px-3 py-2"
            >
              <div className="min-w-0">
                <span className="text-sm font-medium text-ink">
                  {client.name}
                </span>
                <span className="ml-2 text-[11px] text-ink-3">
                  {client.style_profile
                    ? Object.keys(parseClientStyle(client))
                        .length + " style fields"
                    : "no style yet"}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  onClick={() => openEdit(client)}
                  className="rounded-md px-2 py-1 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
                >
                  Edit
                </button>
                <button
                  onClick={() => void remove(client)}
                  className="rounded-md px-2 py-1 text-xs font-medium text-danger transition-colors hover:bg-surface-3"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>

        {editing !== null && (
          <div className="space-y-3 rounded-lg border border-line bg-surface p-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Client name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                placeholder="e.g. Game Hacking Village"
                className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink placeholder:text-ink-3/60 focus:border-line-strong focus:outline-none"
              />
            </div>
            {styleFields.map((f) => (
              <div key={f.key}>
                <label className="mb-1 block text-sm font-medium text-ink-2">
                  {f.label}
                </label>
                <input
                  type="text"
                  value={style[f.key] ?? ""}
                  onChange={(e) =>
                    setStyle((s) => ({ ...s, [f.key]: e.target.value }))
                  }
                  maxLength={500}
                  placeholder={f.placeholder}
                  className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink placeholder:text-ink-3/60 focus:border-line-strong focus:outline-none"
                />
              </div>
            ))}
            <div>
              <label className="mb-1 block text-sm font-medium text-ink-2">
                Notes
              </label>
              <textarea
                value={style.notes ?? ""}
                onChange={(e) =>
                  setStyle((s) => ({ ...s, notes: e.target.value }))
                }
                rows={3}
                maxLength={500}
                placeholder="Anything the generator should always know about this client's brand…"
                className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink placeholder:text-ink-3/60 focus:border-line-strong focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => void save()}
                disabled={saving || !name.trim()}
                className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
              >
                {saving
                  ? "Saving..."
                  : editing === "new"
                    ? "Add Client"
                    : "Save Client"}
              </button>
              <button
                onClick={close}
                className="rounded-lg px-3 py-2 text-sm font-medium text-ink-2 transition-colors hover:text-ink"
              >
                Cancel
              </button>
            </div>
            {error && <p className="text-xs text-danger">{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
